// PM-10: Astro Journal, personal reading history with pattern insights.
// This creates unique value that ChatGPT cannot replicate:
// accumulated reading data, timing patterns, and astrological self-knowledge.

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface ReadingEntry {
  id:         string;
  feature:    string;
  rating:     number | null;
  created_at: string;
}

interface FeaturePattern {
  feature: string;
  count:   number;
  pct:     number;
}

const FEATURE_LABELS: Record<string, string> = {
  'forecast':        'Previsão ✦',
  'birth-chart':     'Mapa Natal ♈',
  'synastry':        'Sinastria ♥',
  'questions':       'Perguntas 🔮',
  'tarot-reading':   'Tarô 🃏',
  'numerology':      'Numerologia 🔢',
  'human-design':    'Design Humano 🧬',
  'vedic-astrology': 'Astrologia Védica 🕉️',
  'dream-journal':   'Sonhos 💤',
  'i-ching':         'I Ching ☯',
  'quiromancy':      'Quiromância 🤚',
  'astromatch':      'AstroMatch 📸',
};

const DAYS_OF_WEEK = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function AstroJournalPage() {
  const { user }                   = useAuth();
  const [entries,    setEntries]   = useState<ReadingEntry[]>([]);
  const [patterns,   setPatterns]  = useState<FeaturePattern[]>([]);
  const [peakDay,    setPeakDay]   = useState<string>('');
  const [loading,    setLoading]   = useState(true);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  async function load() {
    const { data } = await supabase
      .from('reading_analytics')
      .select('id, feature, rating, created_at')
      .eq('user_id', user!.id)
      .order('created_at', { ascending: false })
      .limit(100);

    if (!data) { setLoading(false); return; }

    const rows = data as ReadingEntry[];
    setEntries(rows);

    // Feature distribution
    const counts: Record<string, number> = {};
    for (const r of rows) counts[r.feature] = (counts[r.feature] ?? 0) + 1;
    const total = rows.length || 1;
    const pats = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([feature, count]) => ({ feature, count, pct: Math.round(count / total * 100) }));
    setPatterns(pats);

    // Peak day of week
    const dayCounts: Record<number, number> = {};
    for (const r of rows) {
      const day = new Date(r.created_at).getDay();
      dayCounts[day] = (dayCounts[day] ?? 0) + 1;
    }
    const peakDayNum = Object.entries(dayCounts).sort((a, b) => +b[1] - +a[1])[0];
    if (peakDayNum) setPeakDay(DAYS_OF_WEEK[+peakDayNum[0]]);

    setLoading(false);
  }

  if (loading) {
    return (
      <div className="page-container">
        <p style={{ color: 'var(--text-muted)' }}>Carregando seu registro astrológico…</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="page-container">
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '1rem' }}>
          ✦ Registro Astrológico
        </h1>
        <div className="card" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
          <p style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🌙</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
            Seu diário astrológico está vazio. Faça sua primeira consulta para começar a construir seu registro pessoal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          ✦ Registro Astrológico
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Seu histórico pessoal, padrões que só você possui.
        </p>
      </div>

      {/* Pattern insights */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
          🔭 Seus padrões astrológicos
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
          {patterns.slice(0, 5).map(p => (
            <div key={p.feature} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', minWidth: '140px' }}>
                {FEATURE_LABELS[p.feature] ?? p.feature}
              </span>
              <div style={{ flex: 1, height: '6px', backgroundColor: 'var(--bg-surface)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${p.pct}%`, height: '100%', backgroundColor: 'var(--petrol)', borderRadius: '3px', transition: 'width 0.5s ease' }} />
              </div>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', minWidth: '54px', textAlign: 'right' }}>
                {p.count}× ({p.pct}%)
              </span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>Total de consultas</p>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0.1rem 0 0', fontSize: '1.25rem' }}>{entries.length}</p>
          </div>
          {peakDay && (
            <div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>Dia mais ativo</p>
              <p style={{ fontWeight: 700, color: 'var(--petrol)', margin: '0.1rem 0 0', fontSize: '1.25rem' }}>{peakDay}</p>
            </div>
          )}
          {patterns[0] && (
            <div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>Consulta favorita</p>
              <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0.1rem 0 0', fontSize: '1rem' }}>
                {FEATURE_LABELS[patterns[0].feature] ?? patterns[0].feature}
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
